/**
 * `/policies` — WP-K5, Task 45. `web/shapes/policies.md` is the agreed shape.
 *
 * This is the screen where somebody decides how closely everyone is watched. Its
 * order is deliberate and is not the order of a settings schema: the writable list
 * first, the **diff** physically between the editor and the save button, and the
 * read-only posture below with the reason it is read-only.
 *
 * ── What a save does, and what a refusal does ───────────────────────────────
 *
 * `POST /admin/activity-catalog/websites` is keyed on
 * `(company, scope_type, scope_key, match_field, match_value, effective_from)`;
 * saving under an existing key updates the rule and **increments `version`**. This
 * screen never sends `effective_from`, so a save always lands on the endpoint's
 * default and therefore always *updates* the rule in force rather than quietly
 * creating a second one dated today.
 *
 * A refusal is `400 WORKFORCE_VALIDATION_FAILED` with the offending field in
 * `details.issues`. Three are reachable from here — a `regex` `match_value` that does
 * not compile, an `exact` `match_value` carrying a scheme/path/query/fragment, and a
 * `scope_key` that disagrees with `scope_type`. When one arrives:
 *
 *   - the server's own message is rendered, **verbatim**, not paraphrased: it names
 *     the remedy (`match_field: domain_suffix`) and this client has no second copy of
 *     the server's rules to paraphrase from;
 *   - the field it named is marked `aria-invalid` so the message lands beside the
 *     input rather than at the top of the screen;
 *   - **the previous version is left standing.** Nothing is applied optimistically,
 *     so there is no rollback to get wrong, and the list is not re-read because
 *     nothing happened;
 *   - the diff stays on screen, still labelled *chưa lưu*, so the operator can see
 *     exactly what they were about to commit.
 *
 * ── Two reads, and why the vocabulary is one of them ────────────────────────
 *
 * The rules and the category vocabulary are separate requests.
 * `GET /admin/activity-catalog/categories` is read rather than hard-coded so this
 * screen cannot offer a category the server would refuse — the same reason
 * `CollectionRules` uses an exhaustive `Record` for its labels: a sixteenth category
 * must be a compile error here and a rendered option there, never a silent `400`.
 */

import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { Link } from 'react-router';

import { useI18n } from '../i18n';
import {
  websiteCatalogPath,
  ADMIN_CATALOG_CATEGORIES_PATH,
  ADMIN_WEB_CATALOG_PATH,
} from '../api/paths';
import type {
  ActivityCategory,
  CategoryVocabularyResponse,
  WebsiteCatalogEntry,
  WebsiteCatalogEntryRequest,
  WebsiteCatalogListResponse,
} from '../api/types';
import { ACTIVITY_CATEGORIES } from '../api/types';
import { useApiMutation } from '../api/useApiMutation';
import { useApiQuery } from '../api/useApiQuery';
import { matchExisting, refusedFields, summarise, type WebsiteRuleDraft } from '../policies/diff';
import { ApiErrorNote } from '../states/ApiErrorNote';
import { Skeleton } from '../states/Skeleton';
import { StatusNote } from '../states/StatusNote';
import { ScreenHeading } from '../shell/ScreenHeading';
import { DiffSummary } from './policies/DiffSummary';
import { PosturePanel } from './policies/PosturePanel';
import { PrivateDomains } from './policies/PrivateDomains';

const RULE_PAGE_LIMIT = 200;

const EMPTY_DRAFT: WebsiteRuleDraft = {
  match_value: '',
  match_field: 'domain',
  category: 'uncategorized',
  is_private: false,
  is_blacklisted: false,
  scope_type: 'company',
  scope_key: null,
};

export function PoliciesScreen(): ReactNode {
  const { t } = useI18n();
  const [reloadToken, setReloadToken] = useState(0);
  const [draft, setDraft] = useState<WebsiteRuleDraft>(EMPTY_DRAFT);
  const [acknowledged, setAcknowledged] = useState(false);

  const rules = useApiQuery<WebsiteCatalogListResponse>(
    websiteCatalogPath(RULE_PAGE_LIMIT),
    reloadToken,
  );
  const vocabulary = useApiQuery<CategoryVocabularyResponse>(ADMIN_CATALOG_CATEGORIES_PATH);
  const save = useApiMutation<WebsiteCatalogEntryRequest, WebsiteCatalogEntry>(
    ADMIN_WEB_CATALOG_PATH,
  );

  const rows: readonly WebsiteCatalogEntry[] = rules.data?.data ?? [];
  const total = rules.data?.total ?? rows.length;

  /**
   * The vocabulary, narrowed to values this client has a label for.
   *
   * A category the server publishes and this build has no string for renders as
   * nothing rather than as a raw enum, and the fallback when the read has not landed
   * is the client's own list — which is the same fifteen values, so the editor still
   * works while the second request is in flight.
   */
  const categories: readonly ActivityCategory[] = useMemo(() => {
    const published = vocabulary.data?.data;
    if (published === undefined) return ACTIVITY_CATEGORIES;
    const known = new Set<string>(ACTIVITY_CATEGORIES);
    return published
      .map((entry) => entry.category)
      .filter((category): category is ActivityCategory => known.has(category));
  }, [vocabulary.data]);

  const existing = useMemo(() => matchExisting(rows, draft), [rows, draft]);
  const changes = useMemo(
    () => (draft.match_value.trim() === '' ? [] : summarise(draft, existing)),
    [draft, existing],
  );
  const refused = save.error === null ? [] : refusedFields(save.error.details);

  /**
   * Only a **success** re-reads the list.
   *
   * This was `.then(...)` on `submit`, and `useApiMutation.submit` **resolves rather
   * than rejects** — deliberately, so a form's synchronous handler cannot leave an
   * unhandled rejection in the console — which meant the continuation ran on a
   * refusal too and re-read the catalogue after a request that changed nothing.
   * `policies.test.tsx` caught it by counting the reads. Keyed on `save.data`
   * instead, which is set only by a `201`, so the branch cannot be reached by a
   * refusal at all rather than being guarded against one.
   */
  const saved = save.data;
  useEffect(() => {
    if (saved === null) return;
    setReloadToken((value) => value + 1);
  }, [saved]);

  const submit = (): void => {
    save
      .submit({
        match_value: draft.match_value.trim(),
        category: draft.category,
        match_field: draft.match_field,
        is_private: draft.is_private,
        is_blacklisted: draft.is_blacklisted,
        scope_type: draft.scope_type,
        ...(draft.scope_type === 'company' ? {} : { scope_key: draft.scope_key }),
      })
      .catch(() => {
        /* `submit` resolves rather than rejects; this is unreachable and present so
           the promise chain cannot become an unhandled rejection if that changes. */
      });
  };

  return (
    <div className="wp-stack">
      <ScreenHeading>{t('nav.policies')}</ScreenHeading>
      <p className="wp-small wp-muted">{t('policies.intro')}</p>

      {rules.status === 'loading' ? (
        <Skeleton
          rows={5}
          variant="block"
          label={t('policies.subject')}
          testId="k5-rules-skeleton"
        />
      ) : null}
      {rules.status === 'error' && rules.error !== null ? (
        <ApiErrorNote
          error={rules.error}
          onRetry={rules.reload}
          subject={t('policies.subject')}
          testId="k5-rules-error"
        />
      ) : null}

      {rules.data === null ? null : (
        <>
          <PrivateDomains
            rules={rows}
            total={total}
            categories={categories}
            draft={draft}
            onDraft={(next) => {
              setDraft(next);
              // A changed draft is a different decision, so a previous acknowledgement
              // does not carry over into it.
              setAcknowledged(false);
            }}
            refused={refused}
            refusalNote={
              save.status === 'error' && save.error !== null ? (
                <StatusNote
                  severity="high"
                  role="alert"
                  title={t('policies.refused.title')}
                  testId="k5-save-refused"
                >
                  {/* The server's own words. This client keeps no second copy of the
                      rules it would have to paraphrase from. */}
                  <p data-testid="k5-save-refused-message">{save.error.message}</p>
                  {refused.length === 0 ? null : (
                    <p data-testid="k5-save-refused-fields">
                      {t('policies.refused.field', { fields: refused.join(', ') })}
                    </p>
                  )}
                  <p>{t('policies.refused.kept')}</p>
                </StatusNote>
              ) : null
            }
          />

          <DiffSummary
            changes={changes}
            fromVersion={existing?.version ?? null}
            acknowledged={acknowledged}
            saving={save.status === 'pending'}
            onAcknowledge={setAcknowledged}
            onSave={submit}
          />

          {save.status === 'ok' && save.data !== null ? (
            <StatusNote
              severity="low"
              title={t('policies.saved', {
                host: save.data.match_value,
                version: save.data.version,
              })}
              testId="k5-save-ok"
            />
          ) : null}
        </>
      )}

      <PosturePanel />

      <section className="wp-section" aria-labelledby="k5-version-heading">
        <h2 className="wp-section__title" id="k5-version-heading">
          {t('policies.version.title')}
        </h2>
        <p>{t('policies.version.body')}</p>
        <p className="wp-small wp-muted">{t('policies.version.where')}</p>
        <Link className="wp-linklike" to="/devices" data-testid="k5-version-link">
          {t('policies.version.link')}
        </Link>
      </section>
    </div>
  );
}
